/**
 * The service-worker update signal (13-08, RESEARCH Pitfall 6).
 *
 * main.tsx registers the worker outside React. When an updated worker has installed and is
 * waiting, it is handed to this tiny store; `UpdateBanner` subscribes (useSyncExternalStore)
 * and offers a calm, non-blocking "A new version is ready — Reload". Nothing is forced: the old
 * blocking `confirm()` would have put a modal in front of every head-office user on the
 * redesign's first load.
 *
 * Reload = post SKIP_WAITING to the waiting worker; main.tsx reloads on `controllerchange`.
 * No React here, so it is unit-tested in the node environment.
 */

/** The part of a ServiceWorker this store needs (kept structural for tests). */
export interface WaitingWorker {
  postMessage(message: unknown): void;
}

export interface UpdateState {
  /** True while an updated worker is waiting and the banner has not been dismissed. */
  ready: boolean;
}

type Listener = () => void;

const IDLE: UpdateState = { ready: false };
const READY: UpdateState = { ready: true };

let waiting: WaitingWorker | null = null;
let state: UpdateState = IDLE;
const listeners = new Set<Listener>();

function emit(next: UpdateState): void {
  if (next === state) return;
  state = next;
  listeners.forEach((l) => l());
}

/** main.tsx: an updated worker has installed and is waiting to take over. */
export function offerUpdate(worker: WaitingWorker): void {
  waiting = worker;
  emit(READY);
}

/** The banner's Reload: ask the waiting worker to take over (main.tsx then reloads). */
export function applyUpdate(): boolean {
  if (!waiting) return false;
  waiting.postMessage({ type: 'SKIP_WAITING' });
  return true;
}

/** The banner's "Later": hide it for this page load. The worker stays waiting. */
export function dismissUpdate(): void {
  emit(IDLE);
}

export function subscribeUpdate(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function getUpdateState(): UpdateState {
  return state;
}

/** Tests only. */
export function resetUpdateSignal(): void {
  waiting = null;
  state = IDLE;
  listeners.clear();
}
